import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useContext} from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

import {isInaccessibleInfo} from 'auth/RequestAccess';
import BenchlingConfig from 'BenchlingConfig';
import ChartContent, {QueryingChartContent} from 'charts/chart-content/ChartContent';
import {GetChart, GetChartVariables} from 'charts/network/__generated/GetChart';
import {GET_CHART_QUERY} from 'charts/network/GetChart';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {AnalysisWorkspaceContext} from 'llm/chat-library/layouts/analysis-workspace/AnalysisWorkspaceContext';
import ChatUploadedFileChip from 'llm/chat-library/messages/ChatUploadedFileChip';
import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import CitationSuperscript from 'llm/markdown/CitationSuperscript';
import {rehypeBenchlingTags} from 'llm/markdown/LLMMarkdownRehype';
import {remarkBenchlingTags, remarkCodeBlock} from 'llm/markdown/LLMMarkdownRemark';
import {
  BenchlingTagConfig,
  BenchlingTagConfigs,
  BenchlingTagProperty,
  isAllowedUrl,
  parseBnchFileJSON,
} from 'llm/markdown/LLMMarkdownUtils';
import TableWithCitations from 'llm/markdown/TableWithCitations';
import {InternalLink, isModelInternalLinkable} from 'notebook/mediocre/mentions/InternalLink';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip, {isModelBenchChipSupported} from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {remarkGfmTables} from 'ui-library/remarkGfmTables';
import {getAPIIDPrefix} from 'util/APIUtil';
import * as logging from 'util/Logging';

interface LLMMarkdownDependencies {
  onCitationClick?: (toolId: string, isSourceCitation?: boolean) => void;
  progressData?: ProgressData;
  citationNumberMap?: Map<string, number>;
  chatSessionId?: string;
}

export enum SaveToEntryOption {
  REMOVE = 'remove',
  PRESERVE_TEXT = 'preserve_text',
  PRESERVE_TAGS = 'preserve_tags',
}

function BenchlingIdChip({idValue}: Record<string, unknown>): JSX.Element {
  const id = idValue as string;
  if (id && isModelBenchChipSupported(id)) {
    return <BenchChip id={id} size={ChipSize.MINI} />;
  }
  const modelType = id ? getAPIIDPrefix(id) : undefined;
  logging.info(`[llm] Invalid Benchling ID`, {extra: {id, modelType}});
  return <span>{id}</span>;
}

function BenchlingIdSavingComponent({idValue}: Record<string, unknown>): JSX.Element {
  const id = idValue as string;
  if (id && isModelInternalLinkable(id)) {
    return <InternalLink internalLink={{id, name: id}} />;
  }
  return <span>{id}</span>;
}

function CitationChipComponent({
  idValue,
  deps,
}: Record<string, unknown> & {deps: LLMMarkdownDependencies}): JSX.Element {
  const citationId = idValue as string;
  const citationNumber = deps.citationNumberMap?.get(citationId);
  return (
    <CitationSuperscript
      citationId={citationId}
      citationNumber={citationNumber}
      onCitationClick={deps.onCitationClick}
      progressData={deps.progressData}
    />
  );
}

function ParagraphComponent({children}: Record<string, unknown>): JSX.Element {
  return <div className={css(styles.paragraph)}>{children}</div>;
}

function ViewCodeButton({onClick, className}: {onClick: () => void; className?: string}): JSX.Element {
  return (
    <div
      className={`${css(styles.viewCode)} ${className ?? ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <BenchlingIcon icon="code" /> View code
    </div>
  );
}

function ChartComponent({idValue}: Record<string, unknown>): JSX.Element {
  const chartId = idValue as string;
  const workspaceContext = useContext(AnalysisWorkspaceContext);

  if (!workspaceContext) {
    return (
      <div className={css(styles.chartContainer)} data-chart-id={chartId}>
        <QueryingChartContent chartId={chartId} />
      </div>
    );
  }

  return (
    <div className={css(styles.chartContainer)} data-chart-id={chartId}>
      <BenchlingQuery<GetChart, GetChartVariables>
        query={GET_CHART_QUERY}
        renderLoading={() => <CenteredHorizontalThrobber />}
        variables={{chartId}}
      >
        {(data) => {
          if (isInaccessibleInfo(data.chart)) {
            return (
              <InlineBanner
                bannerType={InlineBannerType.ERROR}
                text="You do not have access to this chart."
              />
            );
          }

          const sourceCode =
            (data.chart.libraryConfig._sourceCode as string | undefined) ??
            (data.chart.libraryConfig._untrustedSourceCode as string | undefined);
          return (
            <>
              <div className={css(styles.chartContent)}>
                <ChartContent chart={data.chart} />
              </div>
              {sourceCode != null && (
                <ViewCodeButton
                  className={css(styles.chartViewCode)}
                  onClick={() => {
                    workspaceContext.openCode(sourceCode, 'python', 'Chart source code');
                  }}
                />
              )}
            </>
          );
        }}
      </BenchlingQuery>
    </div>
  );
}

function TableComponent({children}: Record<string, unknown>): JSX.Element {
  return <TableWithCitations>{children}</TableWithCitations>;
}

function FileComponent({
  fileId,
  filename,
  mediaType,
  deps,
}: Record<string, unknown> & {deps: LLMMarkdownDependencies}): JSX.Element {
  return (
    <ChatUploadedFileChip
      chatSessionId={deps.chatSessionId ?? null}
      externalFileId={fileId as string}
      filename={filename as string}
      mediaType={mediaType as string}
    />
  );
}

function WebCitationComponent({linkUrl}: Record<string, unknown>): JSX.Element {
  const originalUrl = linkUrl as string;
  // Strip all parameters from the URL as a security mitigation
  let cleanUrl = '';
  try {
    const url = new URL(originalUrl);
    cleanUrl = `${url.origin}${url.pathname}`;
  } catch (error) {
    if (error instanceof TypeError) {
      return <></>;
    }
    throw error;
  }

  return (
    <span className={css(styles.webCitationContainer)}>
      <a href={cleanUrl} target="_blank" rel="noopener noreferrer" className={css(styles.webCitationLink)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          icon="link"
          {...getTestProps({
            component: 'LLMMarkdownSection',
            element: 'webCitationButton',
            key: cleanUrl,
          })}
        />
      </a>
    </span>
  );
}

/**
 * Factory function to create tag configs with dependencies
 *
 * Warning: Do NOT add inline components here, they will be regenerated on every render.
 */
export const createBenchlingTagConfigs = (deps: LLMMarkdownDependencies): BenchlingTagConfigs => {
  const tagConfigs: Array<BenchlingTagConfig> = [
    {
      markdownTag: 'bnch_id',
      htmlTagName: 'idchip',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => /^[a-z]+_[a-zA-Z0-9]{8,16}$/.test(content),
      component: BenchlingIdChip,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TAGS,
      savingComponent: BenchlingIdSavingComponent,
    },
    {
      markdownTag: 'bnch_citation',
      htmlTagName: 'citationchip',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => content.length > 0,
      component: (props: Record<string, unknown>) => <CitationChipComponent {...props} deps={deps} />,
      preserveContentWhenSaving: SaveToEntryOption.REMOVE,
    },
    // This is a hack to allow block elements like charts to be rendered.
    // This pattern should not be used.
    {
      markdownTag: 'p',
      htmlTagName: 'p',
      properties: [],
      validator: () => true,
      component: ParagraphComponent,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TEXT,
    },
    {
      markdownTag: 'bnch_chart',
      htmlTagName: 'benchlingchart',
      properties: [BenchlingTagProperty.ID_VALUE],
      validator: (content: string) => /^chart_[a-zA-Z0-9]{8,16}$/.test(content),
      component: ChartComponent,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TEXT,
    },
    {
      markdownTag: 'bnch_file',
      htmlTagName: 'benchlingfile',
      properties: [
        BenchlingTagProperty.FILE_ID,
        BenchlingTagProperty.FILENAME,
        BenchlingTagProperty.MEDIA_TYPE,
      ],
      validator: (content: string) => parseBnchFileJSON(content) != null,
      // TODO: Avoid this inline definition to avoid re-mounting on re-renders. Consider using a
      // React context to thread LLMMarkdownDependencies to components.
      component: (props: Record<string, unknown>) => <FileComponent {...props} deps={deps} />,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TEXT,
    },
    {
      markdownTag: null,
      htmlTagName: 'table',
      properties: [],
      validator: (content: string) => content.length > 0,
      component: TableComponent,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TEXT,
    },
  ];
  if (BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA) {
    tagConfigs.push({
      markdownTag: 'web_citation',
      htmlTagName: 'webcitationchip',
      properties: [BenchlingTagProperty.LINK_URL],
      validator: (content: string) => {
        try {
          const url = new URL(content);
          return url.protocol === 'http:' || url.protocol === 'https:';
        } catch (error) {
          // Expected: URL constructor throws for invalid URLs, return false for validation
          if (error instanceof TypeError) {
            return false;
          }
          throw error;
        }
      },
      component: WebCitationComponent,
      preserveContentWhenSaving: SaveToEntryOption.PRESERVE_TEXT,
    });
  }
  return {tagConfigs};
};

const BASE_SAFE_MARKDOWN_TAGS = [
  // NOTE: We purposefully don't include img, script, etc. to limit risk.
  // NOTE: anchor tags (a) are allowed but hrefs are restricted to allowlisted domains only
  // headers
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  // text
  'p',
  'span',
  // rule & break
  'hr',
  'br',
  // code
  'code',
  'pre',
  'bnch_code',
  // lists
  'li',
  'ul',
  'ol',
  // italics
  'em',
  // bold
  'strong',
  'b',
  // quotes
  'blockquote',
  'q',
  // strikethrough
  's',
  'del',
  // tables
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  // links (with allowlist restriction)
  'a',
];

// Creates safe markdown tags list including custom elements from config
const createSafeMarkdownTags = (tagConfigs: BenchlingTagConfigs): Array<string> => [
  ...BASE_SAFE_MARKDOWN_TAGS,
  ...tagConfigs.tagConfigs.map((config) => config.htmlTagName),
];

// Creates a sanitize schema for the markdown tags
export const createSanitizeSchema = (
  tagConfigs: BenchlingTagConfigs
): {
  tagNames: Array<string>;
  attributes: Record<string, Array<string>>;
} => ({
  tagNames: createSafeMarkdownTags(tagConfigs),
  attributes: {
    '*': [],
    // Allow href, target, and rel on anchor tags (validation happens in custom component)
    a: ['href', 'target', 'rel'],
    // Allow language and code on bnch_code elements (set by remarkCodeBlock plugin)
    bnch_code: ['language', 'code'],
    ...Object.fromEntries(tagConfigs.tagConfigs.map((config) => [config.htmlTagName, config.properties])),
  },
});

export interface LLMMarkdownProps {
  citationNumberMap?: Map<string, number>;
  markdown: string;
  onCitationClick?: (toolId: string, isSourceCitation?: boolean) => void;
  progressData?: ProgressData;
  chatSessionId?: string;
}

/**
 * Component for fenced code blocks, rendered from the custom `bnch_code` element
 * produced by the remarkCodeBlock plugin.
 *
 * The `language` and `code` props are set directly from the markdown AST's `code` node,
 * which has `lang` and `value` as first-class properties.
 *
 * When AnalysisWorkspaceContext is available, shows a "View code" button.
 * Otherwise, falls back to rendering a normal <pre><code>.
 */
function CodeBlockComponent(props: Record<string, unknown>): JSX.Element {
  const language = (props.language as string) ?? 'text';
  const code = (props.code as string) ?? '';
  const workspaceContext = useContext(AnalysisWorkspaceContext);

  if (!workspaceContext) {
    return (
      <pre>
        <code>{code}</code>
      </pre>
    );
  }

  return (
    <div className={css(styles.codeBlock)}>
      <ViewCodeButton
        onClick={() => {
          workspaceContext.openCode(code, language, `${language} code`);
        }}
      />
    </div>
  );
}

/**
 * Custom anchor tag component that validates URLs against allowlist.
 * Renders allowed URLs as links with security attributes, otherwise renders as plain text.
 */
function AllowlistAnchor({href, children}: React.AnchorHTMLAttributes<HTMLAnchorElement>): JSX.Element {
  // If no href or href is not in allowlist, render as plain text
  if (!href || !isAllowedUrl(href)) {
    return <span>{children}</span>;
  }
  // For allowed URLs, render as link with security attributes
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

export default function LLMMarkdownSection({
  citationNumberMap,
  markdown,
  onCitationClick,
  progressData,
  chatSessionId,
}: LLMMarkdownProps): JSX.Element {
  const tagConfigs = createBenchlingTagConfigs({
    onCitationClick,
    progressData,
    citationNumberMap,
    chatSessionId,
  });
  const components: Record<string, React.ComponentType<Record<string, unknown>>> = {
    ...Object.fromEntries(tagConfigs.tagConfigs.map((config) => [config.htmlTagName, config.component])),
    a: AllowlistAnchor as React.ComponentType<Record<string, unknown>>,
    bnch_code: CodeBlockComponent,
  };

  const sanitizeSchema = createSanitizeSchema(tagConfigs);

  return (
    <section {...getTestProps({component: 'LLMMarkdownSection', element: 'root'})}>
      <ReactMarkdown
        allowedElements={sanitizeSchema.tagNames}
        remarkPlugins={[[remarkBenchlingTags, tagConfigs], remarkGfmTables, remarkCodeBlock]}
        rehypePlugins={[
          [rehypeBenchlingTags, tagConfigs],
          [rehypeSanitize, sanitizeSchema],
        ]}
        components={components}
      >
        {markdown}
      </ReactMarkdown>
    </section>
  );
}

/** Height for chart blocks in the LLM markdown view (px). */
const LLM_CHART_HEIGHT = 450;

const styles = StyleSheet.create({
  webCitationContainer: {
    display: 'inline-block',
    marginLeft: '2px',
    marginRight: '2px',
  },
  webCitationLink: {
    textDecoration: 'none',
    color: 'inherit',
  },
  chartContainer: {
    width: '100%',
    height: LLM_CHART_HEIGHT,
    margin: '16px 0',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '4px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  chartContent: {
    flex: 1,
    minHeight: 0,
  },
  paragraph: {
    margin: '1em 0',
    lineHeight: '1.5',
  },
  codeBlock: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    margin: '8px 0',
  },
  viewCode: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 12px',
    color: GRAY_DARK,
    fontSize: 12,
    cursor: 'pointer',
  },
  chartViewCode: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
  },
});
